import React, { useState } from 'react';
import { Modal, Button, Form, InputGroup, Spinner, Alert } from 'react-bootstrap';
import { useHomeLocation } from '../contexts/HomeLocationContext';

export default function LocationPicker({ show, onHide }) {
  const { homeLocation, setHomeLocation, clearHomeLocation } = useHomeLocation();
  const [addressInput, setAddressInput] = useState('');
  const [geocoding, setGeocoding] = useState(false);
  const [gpsLoading, setGpsLoading] = useState(false);
  const [error, setError] = useState(null);

  const handleUseCurrentLocation = () => {
    if (!navigator.geolocation) {
      setError('Geolocation is not supported by your browser.');
      return;
    }
    setGpsLoading(true);
    setError(null);
    navigator.geolocation.getCurrentPosition(
      (pos) => {
        setHomeLocation({
          lat: pos.coords.latitude,
          lon: pos.coords.longitude,
          label: 'Current Location',
        });
        setGpsLoading(false);
        onHide();
      },
      (err) => {
        setError(`Could not get location: ${err.message}`);
        setGpsLoading(false);
      },
      { timeout: 10000 }
    );
  };

  const handleGeocode = async (e) => {
    e.preventDefault();
    if (!addressInput.trim()) return;
    setGeocoding(true);
    setError(null);
    try {
      const url = `https://nominatim.openstreetmap.org/search?format=json&limit=1&q=${encodeURIComponent(addressInput)}`;
      const resp = await fetch(url, {
        headers: { 'Accept-Language': 'he,en' },
      });
      const data = await resp.json();
      if (!data.length) {
        setError('Address not found. Try a more specific address.');
        setGeocoding(false);
        return;
      }
      const result = data[0];
      setHomeLocation({
        lat: parseFloat(result.lat),
        lon: parseFloat(result.lon),
        label: result.display_name,
      });
      setAddressInput('');
      setGeocoding(false);
      onHide();
    } catch (err) {
      setError('Failed to geocode address. Please try again.');
      setGeocoding(false);
    }
  };

  const handleClear = () => {
    clearHomeLocation();
    onHide();
  };

  return (
    <Modal show={show} onHide={onHide} centered size="md">
      <Modal.Header closeButton>
        <Modal.Title style={{ fontSize: '17px', fontWeight: 600 }}>Set Home Location</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        {homeLocation && (
          <Alert variant="info" className="d-flex justify-content-between align-items-start mb-3">
            <div>
              <strong>Current:</strong>{' '}
              <span style={{ fontSize: '13px', wordBreak: 'break-word' }}>{homeLocation.label}</span>
            </div>
            <Button variant="outline-secondary" size="sm" className="ms-2 flex-shrink-0" onClick={handleClear}>
              Clear
            </Button>
          </Alert>
        )}

        {error && <Alert variant="danger" className="mb-3">{error}</Alert>}

        <div className="d-grid mb-3">
          <Button
            variant="primary"
            onClick={handleUseCurrentLocation}
            disabled={gpsLoading || geocoding}
          >
            {gpsLoading ? <><Spinner size="sm" className="me-2" />Getting location...</> : 'Use my current location'}
          </Button>
        </div>

        <div className="d-flex align-items-center mb-3">
          <hr className="flex-grow-1" />
          <span className="px-2 text-muted" style={{ fontSize: '13px' }}>or enter an address</span>
          <hr className="flex-grow-1" />
        </div>

        <Form onSubmit={handleGeocode}>
          <InputGroup>
            <Form.Control
              placeholder="e.g. Dizengoff St 50, Tel Aviv"
              value={addressInput}
              onChange={(e) => setAddressInput(e.target.value)}
              disabled={gpsLoading || geocoding}
            />
            <Button type="submit" variant="outline-primary" disabled={gpsLoading || geocoding || !addressInput.trim()}>
              {geocoding ? <Spinner size="sm" /> : 'Set'}
            </Button>
          </InputGroup>
          <Form.Text className="text-muted">Distances will be recalculated instantly.</Form.Text>
        </Form>
      </Modal.Body>
    </Modal>
  );
}
